/**
 * 共享地形數據單例
 *
 * 高度圖存放於此模組級緩衝區。
 * ErosionEngine 直接寫入（gl.readPixels），Three.js 組件透過
 * useFrame 檢查 version 變化來更新 DataTexture。
 *
 * 完全不觸發 React 重渲染 — 解決每秒 15 次 setHeightmap +
 * bumpHeightmapVersion 導致的 OOM 與調度器過載。
 */

import {
  HEIGHTMAP_SIZE,
  TERRAIN_HEIGHT_SCALE,
  TERRAIN_WORLD_SIZE,
} from './terrainConstants'

class TerrainBuffer {
  /** 高度圖 RGBA Float32（R 通道 = 0-1 歸一化高度） */
  readonly heightmap: Float32Array
  /** 解析度 */
  readonly size: number

  /** 高度圖版本（每次 readPixels 後遞增） */
  heightmapVersion = 0
  /** 初次讀回完成 */
  ready = false

  /**
   * 有效高度版本 — heightmapVersion 或 smoothed 變更時遞增。
   * PlacedObjects 等用此判斷是否需要重新吸附地面。
   */
  getHeightVersion = 0

  private _smoothed = false

  /** 是否啟用 3×3 高斯平滑（匹配青綠山水 GPU 頂點位移） */
  get smoothed() { return this._smoothed }
  set smoothed(v: boolean) {
    if (this._smoothed !== v) {
      this._smoothed = v
      this.getHeightVersion++
    }
  }

  constructor(size: number) {
    this.size = size
    this.heightmap = new Float32Array(size * size * 4)
  }

  /** 在紋理 UV 空間做雙線性插值（返回 0-1 原始高度） */
  private sampleRawAtTexUV(texU: number, texV: number): number {
    const fx = texU * this.size - 0.5
    const fy = texV * this.size - 0.5
    const x0 = Math.max(0, Math.min(this.size - 2, Math.floor(fx)))
    const y0 = Math.max(0, Math.min(this.size - 2, Math.floor(fy)))
    const dx = fx - x0
    const dy = fy - y0
    const h00 = this.heightmap[(y0 * this.size + x0) * 4] || 0
    const h10 = this.heightmap[(y0 * this.size + x0 + 1) * 4] || 0
    const h01 = this.heightmap[((y0 + 1) * this.size + x0) * 4] || 0
    const h11 = this.heightmap[((y0 + 1) * this.size + x0 + 1) * 4] || 0
    return h00 * (1 - dx) * (1 - dy) + h10 * dx * (1 - dy) + h01 * (1 - dx) * dy + h11 * dx * dy
  }

  /**
   * 從高度圖採樣世界高度（回傳世界 Y 值）。
   * smoothed=true 時複製 TerrainMeshQingLv 的 3×3 高斯核，
   * 使 CPU 高度與 GPU 渲染面完全一致。
   */
  getHeight(worldX: number, worldZ: number): number {
    const texU = worldX / TERRAIN_WORLD_SIZE + 0.5
    const texV = 0.5 - worldZ / TERRAIN_WORLD_SIZE

    if (!this._smoothed) {
      return this.sampleRawAtTexUV(texU, texV) * TERRAIN_HEIGHT_SCALE
    }

    // 匹配 QingLv shader: eps = texelSize * 2（2-texel 半徑）
    const eps = 2 / this.size
    const s = this.sampleRawAtTexUV.bind(this)
    // 高斯核 [1 2 1; 2 4 2; 1 2 1] / 16
    const smoothH = (
      s(texU - eps, texV - eps)     + s(texU, texV - eps) * 2 + s(texU + eps, texV - eps) +
      s(texU - eps, texV    ) * 2 + s(texU, texV       ) * 4 + s(texU + eps, texV    ) * 2 +
      s(texU - eps, texV + eps)     + s(texU, texV + eps) * 2 + s(texU + eps, texV + eps)
    ) / 16
    return smoothH * TERRAIN_HEIGHT_SCALE
  }
}

export const terrainBuffer = new TerrainBuffer(HEIGHTMAP_SIZE)
